export interface ReservationBoardFormPrefs {
  displayName: string;
  resourceNames: string[];
  notes: string;
}

const STORAGE_KEY_PREFIX = 'kensuke:reservation-board-form:';

function storageKey(boardId: number): string {
  return `${STORAGE_KEY_PREFIX}${boardId}`;
}

function parseResourceNames(value: unknown, legacyResourceName?: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
      .map((item) => item.trim());
  }
  if (typeof legacyResourceName === 'string' && legacyResourceName.trim()) {
    return [legacyResourceName.trim()];
  }
  return [];
}

/**
 * 再訪時の既定項目。複数選択は「今この画面での操作」に限り、prefs には先頭1件だけ残す。
 * （全項目復元だと「2件を登録」が初期表示になりやすい）
 */
export function primaryReservationBoardResourceNames(names: string[]): string[] {
  return names.length > 0 ? [names[0]] : [];
}

export function loadReservationBoardFormPrefs(boardId: number): Partial<ReservationBoardFormPrefs> | null {
  try {
    const raw = localStorage.getItem(storageKey(boardId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ReservationBoardFormPrefs> & { resourceName?: string };
    if (parsed == null || typeof parsed !== 'object') return null;
    // 旧データに複数項目が残っていても、読み出し時点で先頭1件に正規化する
    const resourceNames = primaryReservationBoardResourceNames(
      parseResourceNames(parsed.resourceNames, parsed.resourceName)
    );
    return {
      displayName: typeof parsed.displayName === 'string' ? parsed.displayName : '',
      resourceNames,
      notes: typeof parsed.notes === 'string' ? parsed.notes : '',
    };
  } catch {
    return null;
  }
}

export function saveReservationBoardFormPrefs(
  boardId: number,
  prefs: ReservationBoardFormPrefs
): void {
  try {
    // 空配列を書くと次回も「未選択」になりやすい。画面上は未選択でも、記憶は直前の有効選択を残す
    let resourceNames = primaryReservationBoardResourceNames(prefs.resourceNames);
    if (resourceNames.length === 0) {
      const existing = loadReservationBoardFormPrefs(boardId);
      if (existing?.resourceNames && existing.resourceNames.length > 0) {
        resourceNames = primaryReservationBoardResourceNames(existing.resourceNames);
      }
    }
    localStorage.setItem(storageKey(boardId), JSON.stringify({ ...prefs, resourceNames }));
  } catch {
    // localStorage unavailable or quota exceeded — ignore
  }
}

export function filterResourceNamesByBoard(
  selected: string[],
  boardResourceNames: string[]
): string[] {
  const allowed = new Set(boardResourceNames);
  return selected.filter((name) => allowed.has(name));
}

/** 項目リスト変更後も、可能な選択を維持する（空ならボード先頭1件） */
export function reconcileReservationBoardResourceSelection(
  selected: string[],
  boardResourceNames: string[]
): string[] {
  const filtered = filterResourceNamesByBoard(selected, boardResourceNames);
  if (filtered.length > 0) return filtered;
  if (selected.length === 0) return selected;
  return boardResourceNames.length > 0 ? [boardResourceNames[0]] : [];
}

/** ボード上に存在する項目だけ残し、再訪用に先頭1件へ正規化する */
export function resolveReservationBoardPrefResourceNames(
  selected: string[],
  boardResourceNames: string[]
): string[] {
  return primaryReservationBoardResourceNames(
    filterResourceNamesByBoard(selected, boardResourceNames)
  );
}

const CREATE_COUNTRY_STORAGE_KEY = 'kensuke:reservation-board-create:countryCode';
const HOLIDAY_COUNTRY_STORAGE_KEY_PREFIX = 'kensuke:reservation-board-holiday-country:';

function parseStoredCountryCode(code: unknown): string | null {
  if (typeof code !== 'string') return null;
  const upper = code.trim().toUpperCase();
  return upper.length === 2 ? upper : null;
}

/** 予約表作成モーダルで前回選択した国コードを読み込む（未保存時は null） */
export function loadCreateBoardCountryCode(): string | null {
  try {
    return parseStoredCountryCode(localStorage.getItem(CREATE_COUNTRY_STORAGE_KEY));
  } catch {
    return null;
  }
}

/** 予約表作成モーダルで選択した国コードを記憶する */
export function saveCreateBoardCountryCode(code: string): void {
  const parsed = parseStoredCountryCode(code);
  if (!parsed) return;
  try {
    localStorage.setItem(CREATE_COUNTRY_STORAGE_KEY, parsed);
  } catch {
    // localStorage unavailable or quota exceeded — ignore
  }
}

function holidayCountryStorageKey(boardId: number): string {
  return `${HOLIDAY_COUNTRY_STORAGE_KEY_PREFIX}${boardId}`;
}

/** 共有カレンダーでゲストが選んだ祝日表示用の国コード（ブラウザごと） */
export function loadHolidayCountryCode(boardId: number): string | null {
  try {
    return parseStoredCountryCode(localStorage.getItem(holidayCountryStorageKey(boardId)));
  } catch {
    return null;
  }
}

export function saveHolidayCountryCode(boardId: number, code: string): void {
  const parsed = parseStoredCountryCode(code);
  if (!parsed) return;
  try {
    localStorage.setItem(holidayCountryStorageKey(boardId), parsed);
  } catch {
    // localStorage unavailable or quota exceeded — ignore
  }
}
